import React from 'react';
import { Translate } from 'react-localize-redux';
import { Alert, Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';

/**
 * SHER-803 - the inviter's hand-over of each invitee's accept link.
 *
 * The API sends the invite email but never holds the secret the link carries, so it cannot put
 * the link in that email. These links exist only in the inviter's browser, only now: closing this
 * panel loses them (a re-invite issues new ones).
 */
export interface InvitationLinkRow {
    email: string;
    link: string;
    expiresAt: string;
}

export interface InvitationLinksProps {
    investigationName?: string;
    links: InvitationLinkRow[];
    onCopy: (link: string) => void;
    /** Opens the inviter's mail client on the given mailto: URL. */
    onEmail: (mailtoUrl: string) => void;
    onClose: () => void;
}

/**
 * Built only when the inviter clicks, never rendered: Sentry Replay records DOM attributes, so an
 * `href` carrying the link would ship the secret to Sentry. For the same reason the whole panel is
 * `sentry-block` (Replay records nothing inside it).
 */
export function mailtoFor(row: InvitationLinkRow, investigationName?: string): string {
    const subject = investigationName ? `Sherwood - ${investigationName}` : 'Sherwood';
    return `mailto:${encodeURIComponent(row.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(row.link)}`;
}

export default function InvitationLinks({ investigationName, links, onCopy, onEmail, onClose }: InvitationLinksProps) {
    return (
        <Card variant="outlined" className="sentry-block">
            <CardContent>
                <Typography variant="h6" component="h2" gutterBottom><Translate id="invitation.links.title" /></Typography>
                <Alert severity="warning" sx={{ mb: 2 }}><Translate id="invitation.links.body" /></Alert>
                <Stack spacing={2}>
                    {links.map((row) => (
                        <Box key={row.email}>
                            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                                <Typography fontWeight={600}>{row.email}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    <Translate id="invitation.links.expires" /> {new Date(row.expiresAt).toLocaleDateString()}
                                </Typography>
                            </Stack>
                            <TextField value={row.link} size="small" fullWidth InputProps={{ readOnly: true }} inputProps={{ 'aria-label': row.email }} sx={{ my: 1 }} />
                            <Stack direction="row" spacing={1}>
                                <Button size="small" variant="outlined" onClick={() => onCopy(row.link)}><Translate id="invitation.links.copy" /></Button>
                                <Button size="small" variant="outlined" onClick={() => onEmail(mailtoFor(row, investigationName))}><Translate id="invitation.links.email" /></Button>
                            </Stack>
                        </Box>
                    ))}
                </Stack>
                <Box mt={3} textAlign="right">
                    <Button variant="contained" onClick={onClose}><Translate id="invitation.links.done" /></Button>
                </Box>
            </CardContent>
        </Card>
    );
}
